import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface UseCompletionReminderTimerProps {
  lessonId: string;
  completionStatus: string;
  isOnClassActivitiesTab: boolean;
  canShowReminder: (id: string, status: string) => boolean;
  onShow: (id: string) => void;
  onNetworkHide?: (id: string) => void;
}

interface UseCompletionReminderTimerReturn {
  showModal: boolean;
  hideModal: () => void;
}

const TIMER_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const FOCUS_GRACE_PERIOD = 5000; // 5 seconds

const log = (...args: unknown[]) => {
  console.debug('[CompletionReminder]', ...args);
};

export const useCompletionReminderTimer = ({
  lessonId,
  completionStatus,
  isOnClassActivitiesTab,
  canShowReminder,
  onShow,
  onNetworkHide,
}: UseCompletionReminderTimerProps): UseCompletionReminderTimerReturn => {
  const [showModal, setShowModal] = useState(false);
  const elapsedTimeRef = useRef(0);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
  const [isTabFocused, setIsTabFocused] = useState(document.hasFocus());
  // eslint-disable-next-line no-undef
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const location = useLocation();
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasShownModalRef = useRef(false);
  const wasPausedRef = useRef(false);

  // Use refs for pause conditions so they don't trigger effect re-runs
  const isTabVisibleRef = useRef(isTabVisible);
  const isTabFocusedRef = useRef(isTabFocused);
  const isOnlineRef = useRef(isOnline);
  const isVideoPlayingRef = useRef(isVideoPlaying);
  const showModalRef = useRef(showModal);

  // Update refs when state changes
  useEffect(() => {
    isTabVisibleRef.current = isTabVisible;
  }, [isTabVisible]);

  useEffect(() => {
    isTabFocusedRef.current = isTabFocused;
  }, [isTabFocused]);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    isVideoPlayingRef.current = isVideoPlaying;
  }, [isVideoPlaying]);

  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  // Check if slideshow is open
  const isSlideshowOpen =
    location.pathname.includes('/lessonPlan/slides/') ||
    location.pathname.includes('/middleSchoolSlides/') ||
    location.pathname.includes('/highSchoolSlides/');

  // Monitor page visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      const newVisible = !document.hidden;
      log('Tab visibility changed:', newVisible ? 'VISIBLE' : 'HIDDEN');
      setIsTabVisible(newVisible);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Monitor tab focus with grace period
  useEffect(() => {
    const handleBlur = () => {
      log('Tab blur detected, waiting 5 seconds before pausing...');
      // Wait 5 seconds before marking as unfocused
      blurTimeoutRef.current = setTimeout(() => {
        log('Tab marked as UNFOCUSED (5 second grace period elapsed)');
        blurTimeoutRef.current = null; // Clear ref after timeout executes
        setIsTabFocused(false);
      }, FOCUS_GRACE_PERIOD);
    };

    const handleFocus = () => {
      if (blurTimeoutRef.current) {
        log('✅ Tab focus returned WITHIN grace period (< 5s), cancelling unfocus');
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      } else {
        log('✅ Tab FOCUSED (was already unfocused, timer was paused)');
      }
      setIsTabFocused(true);
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      log('Network: ONLINE');
      setIsOnline(true);
    };
    const handleOffline = () => {
      log('Network: OFFLINE');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitor video playback
  useEffect(() => {
    const updateVideoState = () => {
      const videos = document.querySelectorAll('video');
      const anyPlaying = Array.from(videos).some((video) => !video.paused && !video.ended);
      if (anyPlaying !== isVideoPlaying) {
        log('Video playback state changed:', anyPlaying ? 'PLAYING' : 'STOPPED/PAUSED');
      }
      setIsVideoPlaying(anyPlaying);
    };

    const handlePlay = () => updateVideoState();
    const handlePause = () => updateVideoState();
    const handleEnded = () => updateVideoState();

    // Initial check
    updateVideoState();

    // Add listeners to all existing videos
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);
    });

    // Use MutationObserver to detect new videos added to DOM
    const observer = new MutationObserver(() => {
      updateVideoState();
      const newVideos = document.querySelectorAll('video');
      newVideos.forEach((video) => {
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      videos.forEach((video) => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      });
      observer.disconnect();
    };
  }, [location.pathname]); // Re-run when route changes

  // Hide modal when network goes offline
  useEffect(() => {
    if (!isOnline && showModal) {
      log('Network offline while modal visible - hiding modal and resetting timer');
      setShowModal(false);

      // Reset timer state so modal can show again after another 5 minutes
      elapsedTimeRef.current = 0;
      hasShownModalRef.current = false;
      wasPausedRef.current = false;

      // Rollback the "shown" count since this wasn't a real user interaction
      if (onNetworkHide) {
        log('Rolling back shown count due to network interruption');
        onNetworkHide(lessonId);
      }
    }
  }, [isOnline, showModal, lessonId, onNetworkHide]);

  // Main timer logic
  useEffect(() => {
    log('=== Timer Effect Triggered ===');
    log('Lesson ID:', lessonId);
    log('Completion Status:', completionStatus);
    log('Is on Class Activities Tab:', isOnClassActivitiesTab);
    log('Tab Visible:', isTabVisible);
    log('Tab Focused:', isTabFocused);
    log('Is Online:', isOnline);
    log('Video Playing:', isVideoPlaying);
    log('Slideshow Open:', isSlideshowOpen);
    log('Modal Showing:', showModal);

    // Reset timer when lesson changes
    elapsedTimeRef.current = 0;
    setShowModal(false);
    hasShownModalRef.current = false;

    // Check initial conditions
    if (completionStatus === 'Completed') {
      log('❌ Timer NOT started: Lesson already completed');
      return;
    }

    if (!isOnClassActivitiesTab) {
      log('❌ Timer NOT started: Not on Class Activities tab');
      return;
    }

    const canShow = canShowReminder(lessonId, completionStatus);
    log('Can show reminder check:', canShow);

    if (!canShow) {
      log('❌ Timer NOT started: Frequency limits reached');
      return;
    }

    log(`✅ Starting timer - will show reminder after ${TIMER_DURATION / 60000} minutes`);

    // Start interval
    timerIntervalRef.current = setInterval(() => {
      // Check pause conditions using refs (so we don't reset timer on state changes)
      const currentIsSlideshowOpen =
        location.pathname.includes('/lessonPlan/slides/') ||
        location.pathname.includes('/middleSchoolSlides/') ||
        location.pathname.includes('/highSchoolSlides/');

      const shouldPause =
        !isTabVisibleRef.current ||
        !isTabFocusedRef.current ||
        !isOnlineRef.current ||
        isVideoPlayingRef.current ||
        currentIsSlideshowOpen ||
        showModalRef.current ||
        !isOnClassActivitiesTab;

      if (shouldPause) {
        if (!wasPausedRef.current) {
          // Just paused - log the reasons
          const reasons = [];
          if (!isTabVisibleRef.current) reasons.push('tab hidden');
          if (!isTabFocusedRef.current) reasons.push('tab unfocused');
          if (!isOnlineRef.current) reasons.push('offline');
          if (isVideoPlayingRef.current) reasons.push('video playing');
          if (currentIsSlideshowOpen) reasons.push('slideshow open');
          if (showModalRef.current) reasons.push('modal visible');
          if (!isOnClassActivitiesTab) reasons.push('wrong tab');

          log('⏸️  Timer PAUSED - Reasons:', reasons.join(', '));
          log(
            `⏸️  Timer paused at ${Math.floor(elapsedTimeRef.current / 60000)}m ${Math.floor((elapsedTimeRef.current % 60000) / 1000)}s`,
          );
          wasPausedRef.current = true;
        }
        return; // Don't increment when paused
      }

      // Timer is running - check if we just resumed
      if (wasPausedRef.current) {
        log('▶️  Timer RESUMED');
        log(
          `▶️  Resuming at ${Math.floor(elapsedTimeRef.current / 60000)}m ${Math.floor((elapsedTimeRef.current % 60000) / 1000)}s`,
        );
        wasPausedRef.current = false;
      }

      elapsedTimeRef.current += 1000;
      const minutesElapsed = Math.floor(elapsedTimeRef.current / 60000);
      const secondsElapsed = Math.floor((elapsedTimeRef.current % 60000) / 1000);

      // Log every 30 seconds
      if (elapsedTimeRef.current % 30000 === 0) {
        log(`⏱️  Timer running: ${minutesElapsed}m ${secondsElapsed}s elapsed`);
      }

      // Check if we've reached the threshold
      if (elapsedTimeRef.current >= TIMER_DURATION && !hasShownModalRef.current) {
        log('🎉 5 minutes reached! Showing completion reminder modal');
        hasShownModalRef.current = true;
        setShowModal(true);
        onShow(lessonId);
      }
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        log('Timer cleanup - clearing interval');
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [lessonId, completionStatus, isOnClassActivitiesTab, canShowReminder, onShow, location.pathname]);

  const hideModal = useCallback(() => {
    log('Modal dismissed by user - resetting timer to 0');
    setShowModal(false);
    // Reset timer so it can show again after another 5 minutes (subject to frequency limits)
    elapsedTimeRef.current = 0;
    hasShownModalRef.current = false;
    wasPausedRef.current = false;
  }, []);

  return {
    showModal,
    hideModal,
  };
};
